'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';

import { useT } from '@/hooks';
import type { AppNotification } from '@/services/api/notification.api';
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/services/query';
import { formatRelativeTime } from '@/utils';

import { ROUTES } from '@/constants';

const typeIcon = (type: string) => {
  switch (type) {
    case 'referral_reward':
      return <GroupsIcon sx={{ fontSize: 18 }} />;
    case 'price_alert':
      return <NotificationsActiveIcon sx={{ fontSize: 18 }} />;
    case 'trade':
    default:
      return <SwapHorizIcon sx={{ fontSize: 18 }} />;
  }
};

const typeColor = (type: string) => {
  switch (type) {
    case 'referral_reward':
      return '#22c55e';
    case 'price_alert':
      return '#f59e0b';
    default:
      return '#2563eb';
  }
};

/**
 * Header notification bell + panel. Badge count polls independently; the list is
 * fetched only when the panel is open. Reuses the notifications API (Porvenix data
 * only). Renders nothing structural for guests — mounted only in authed header.
 */
export const NotificationBell = () => {
  const { t } = useT();
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data, isLoading } = useNotifications(20, open);
  const markRead = useMarkNotificationsRead();

  const notifications: AppNotification[] = data?.data ?? [];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAll = () => {
    if (unreadCount > 0) markRead.mutate(undefined);
  };

  const handleClick = (n: AppNotification) => {
    if (!n.isRead) markRead.mutate([n.id]);
    const slug = n.data?.marketSlug as string | undefined;
    if (n.type === 'referral_reward') {
      router.push(ROUTES.REFERRALS);
    } else if (slug) {
      router.push(ROUTES.MARKET_DETAILS(slug));
    }
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t('notifications.title', { defaultValue: 'Notifications' })}
        sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          color="error"
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: '0.62rem' } }}
        >
          {unreadCount > 0 ? (
            <NotificationsActiveIcon sx={{ fontSize: 24 }} />
          ) : (
            <NotificationsNoneIcon sx={{ fontSize: 24 }} />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 320, sm: 380 },
              maxWidth: '95vw',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            {t('notifications.title', { defaultValue: 'Notifications' })}
          </Typography>
          <Button
            size="small"
            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
            onClick={handleMarkAll}
            disabled={unreadCount === 0 || markRead.isPending}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}
          >
            {t('notifications.markAllRead', { defaultValue: 'Mark all read' })}
          </Button>
        </Box>
        <Divider />

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={22} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', px: 3 }}>
              <NotificationsNoneIcon sx={{ fontSize: 34, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('notifications.empty', { defaultValue: "You're all caught up." })}
              </Typography>
            </Box>
          ) : (
            notifications.map((n) => {
              const color = typeColor(n.type);
              return (
                <Box
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClick(n)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick(n);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: n.isRead ? 'transparent' : 'action.hover',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'action.selected' },
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color,
                      bgcolor: `${color}1f`,
                    }}
                  >
                    {typeIcon(n.type)}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                      {n.title}
                    </Typography>
                    {n.body && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                        noWrap
                      >
                        {n.body}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {formatRelativeTime(n.createdAt)}
                    </Typography>
                  </Box>
                  {!n.isRead && (
                    <Box
                      sx={{
                        flexShrink: 0,
                        alignSelf: 'center',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};
